/**
 * Job Status and Interaction UI Component (§8, SAAS-410).
 *
 * Provides:
 * - Real-time progress display (queued, claimed, processing, completed, failed, cancelled)
 * - Cancel button for running jobs
 * - Retry button for failed or cancelled jobs
 * - Explicit result branch action button for completed jobs
 * - Accessible keyboard navigation and dialog dismissal
 */

import type { PendingJobRecord, JobState } from "./types.ts";

export interface JobUIOptions {
  container?: HTMLElement;
  onCancel?: (jobId: string) => Promise<void>;
  onRetry?: (jobId: string) => Promise<void>;
  onApplyResult?: (job: PendingJobRecord) => Promise<void>;
  onDismiss?: () => void;
}

export class JobUI {
  private readonly container: HTMLElement;
  private readonly onCancel?: (jobId: string) => Promise<void>;
  private readonly onRetry?: (jobId: string) => Promise<void>;
  private readonly onApplyResult?: (job: PendingJobRecord) => Promise<void>;
  private readonly onDismiss?: () => void;

  private modalElement: HTMLElement | null = null;
  private currentJob: PendingJobRecord | null = null;
  private timerInterval: any = null;

  constructor(options: JobUIOptions = {}) {
    this.container = options.container || document.body;
    this.onCancel = options.onCancel;
    this.onRetry = options.onRetry;
    this.onApplyResult = options.onApplyResult;
    this.onDismiss = options.onDismiss;
  }

  public show(job: PendingJobRecord): void {
    this.currentJob = job;
    if (!this.modalElement) {
      this.createModal();
    }
    this.update(job);
  }

  public hide(): void {
    this.stopTimer();
    if (this.modalElement && this.modalElement.parentNode) {
      this.modalElement.parentNode.removeChild(this.modalElement);
    }
    this.modalElement = null;
  }

  public update(job: PendingJobRecord): void {
    this.currentJob = job;
    if (!this.modalElement) return;

    const statusBadge = this.modalElement.querySelector<HTMLElement>("#p2i-job-status-badge");
    const phaseText = this.modalElement.querySelector<HTMLElement>("#p2i-job-phase-text");
    const elapsedText = this.modalElement.querySelector<HTMLElement>("#p2i-job-elapsed-text");
    const cancelBtn = this.modalElement.querySelector<HTMLButtonElement>("#p2i-job-cancel-btn");
    const retryBtn = this.modalElement.querySelector<HTMLButtonElement>("#p2i-job-retry-btn");
    const applyBtn = this.modalElement.querySelector<HTMLButtonElement>("#p2i-job-apply-btn");
    const closeBtn = this.modalElement.querySelector<HTMLButtonElement>("#p2i-job-close-btn");

    const state = job.state;
    const isRunning = ["queued", "claimed", "processing"].includes(state);

    if (statusBadge) {
      statusBadge.className = `p2i-badge p2i-badge-${state}`;
      statusBadge.textContent = this.formatStateTitle(job);
    }

    if (phaseText) {
      phaseText.textContent = this.formatPhaseDescription(job);
    }

    if (elapsedText) {
      const elapsedSec = Math.max(0, Math.floor((Date.now() - job.submittedAt) / 1000));
      elapsedText.textContent = `Geçen Süre: ${elapsedSec} sn`;
    }

    if (cancelBtn) {
      cancelBtn.style.display = isRunning ? "inline-block" : "none";
    }

    if (retryBtn) {
      retryBtn.style.display = ["failed", "cancelled"].includes(state) ? "inline-block" : "none";
    }

    if (applyBtn) {
      applyBtn.style.display = state === "completed" ? "inline-block" : "none";
      if (job.isDetachedBranch) {
        applyBtn.textContent = "Dönüştürme Dalına Geç (Yeni Dal)";
      } else {
        applyBtn.textContent = "Sonucu Belgeye Uygula";
      }
    }

    if (closeBtn) {
      closeBtn.style.display = !isRunning ? "inline-block" : "none";
    }

    if (isRunning) {
      this.startTimer();
    } else {
      this.stopTimer();
    }
  }

  private startTimer(): void {
    if (this.timerInterval) return;
    this.timerInterval = setInterval(() => {
      if (!this.currentJob || !this.modalElement) return;
      const elapsedText = this.modalElement.querySelector<HTMLElement>("#p2i-job-elapsed-text");
      if (elapsedText) {
        const elapsedSec = Math.max(0, Math.floor((Date.now() - this.currentJob.submittedAt) / 1000));
        elapsedText.textContent = `Geçen Süre: ${elapsedSec} sn`;
      }
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  private formatStateTitle(job: PendingJobRecord): string {
    switch (job.state) {
      case "queued":
        return `Kuyrukta Bekliyor (Deneme: ${job.attempt}/${job.maxAttempts})`;
      case "claimed":
        return "İş Alındı (Hazırlanıyor)";
      case "processing":
        return "İşleniyor (Model Çalışıyor)";
      case "completed":
        return "Tamamlandı";
      case "failed":
        return `Başarısız Oldu ${job.lastErrorCode ? `(${job.lastErrorCode})` : ""}`;
      case "cancelled":
        return "İptal Edildi";
      default:
        return job.state;
    }
  }

  private formatPhaseDescription(job: PendingJobRecord): string {
    switch (job.state) {
      case "queued":
        return "İşlem kuyrukta boş worker bekliyor. Sayfayı yenileseniz de işlem arka planda devam eder.";
      case "claimed":
        return "Worker işi teslim aldı, model bellek alanı ayrılıyor.";
      case "processing":
        return "Yapay zeka modeli çizimleri ayrıştırıyor ve vektörel nesneleri hesaplıyor...";
      case "completed":
        return job.isDetachedBranch
          ? "Dönüştürme tamamlandı! Mevcut düzenlemelerinizi ezmemek için sonuç ayrı bir dönüştürme dalı olarak hazırlandı."
          : "Dönüştürme başarıyla tamamlandı ve yeni revizyon olarak kaydedildi.";
      case "failed":
        return `Dönüştürme işlemi sırasında bir hata oluştu: ${job.lastErrorCode || "Bilinmeyen hata"}. Yeniden deneyebilirsiniz.`;
      case "cancelled":
        return "Dönüştürme işlemi iptal edildi ve ayrılan kota iade edildi.";
      default:
        return "";
    }
  }

  private createModal(): void {
    const modal = document.createElement("div");
    modal.id = "p2i-job-modal";
    modal.className = "p2i-job-modal-backdrop";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.setAttribute("aria-labelledby", "p2i-job-modal-title");

    modal.innerHTML = `
      <div class="p2i-job-modal-card">
        <div class="p2i-job-modal-header">
          <h3 id="p2i-job-modal-title">Model Dönüştürme Durumu</h3>
        </div>
        <div class="p2i-job-modal-body">
          <div class="p2i-job-status-row">
            <span id="p2i-job-status-badge" class="p2i-badge">Kuyrukta</span>
            <span id="p2i-job-elapsed-text" class="p2i-job-elapsed">Geçen Süre: 0 sn</span>
          </div>
          <p id="p2i-job-phase-text" class="p2i-job-phase-desc"></p>
        </div>
        <div class="p2i-job-modal-actions">
          <button id="p2i-job-cancel-btn" class="p2i-btn p2i-btn-danger">İşlemi İptal Et</button>
          <button id="p2i-job-retry-btn" class="p2i-btn p2i-btn-warning" style="display:none;">Yeniden Dene</button>
          <button id="p2i-job-apply-btn" class="p2i-btn p2i-btn-primary" style="display:none;">Sonucu Belgeye Uygula</button>
          <button id="p2i-job-close-btn" class="p2i-btn p2i-btn-secondary" style="display:none;">Kapat</button>
        </div>
      </div>
    `;

    const cancelBtn = modal.querySelector<HTMLButtonElement>("#p2i-job-cancel-btn");
    const retryBtn = modal.querySelector<HTMLButtonElement>("#p2i-job-retry-btn");
    const applyBtn = modal.querySelector<HTMLButtonElement>("#p2i-job-apply-btn");
    const closeBtn = modal.querySelector<HTMLButtonElement>("#p2i-job-close-btn");

    cancelBtn?.addEventListener("click", async () => {
      if (!this.currentJob) return;
      cancelBtn.disabled = true;
      cancelBtn.textContent = "İptal Ediliyor...";
      try {
        await this.onCancel?.(this.currentJob.jobId);
      } finally {
        cancelBtn.disabled = false;
        cancelBtn.textContent = "İşlemi İptal Et";
      }
    });

    retryBtn?.addEventListener("click", async () => {
      if (!this.currentJob) return;
      retryBtn.disabled = true;
      retryBtn.textContent = "Yeniden Başlatılıyor...";
      try {
        await this.onRetry?.(this.currentJob.jobId);
      } finally {
        retryBtn.disabled = false;
        retryBtn.textContent = "Yeniden Dene";
      }
    });

    applyBtn?.addEventListener("click", async () => {
      if (!this.currentJob) return;
      applyBtn.disabled = true;
      applyBtn.textContent = "Uygulanıyor...";
      try {
        await this.onApplyResult?.(this.currentJob);
        this.hide();
      } finally {
        applyBtn.disabled = false;
      }
    });

    closeBtn?.addEventListener("click", () => {
      this.hide();
      this.onDismiss?.();
    });

    // Handle Escape key
    modal.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Escape" && this.currentJob && !["queued", "claimed", "processing"].includes(this.currentJob.state)) {
        this.hide();
        this.onDismiss?.();
      }
    });

    this.container.appendChild(modal);
    this.modalElement = modal;
  }
}
